import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import axios from "@/features/axiosHelper";
import { logOut } from "@/features/auth/authSlice";
import { useDispatch } from "react-redux";
import { LoadingAnimation } from "@/components/shared/LoadingBar";
import { CloseIconFill } from "@/ImageRepo";
import ShepButton from "@/components/GenericComponents/ShepButton";
import ShepTooltip from "@/components/GenericComponents/ShepTooltip";

const MIN_DISPLAY_MS = 5000;

const TermsConditionModal = ({ open, termsConditionId, htmlContent, onAccepted, onDeclined }) => {
  const dispatch = useDispatch();
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [timerElapsed, setTimerElapsed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state each time the modal opens
  useEffect(() => {
    if (!open) return;
    setScrolledToBottom(false);
    setTimerElapsed(false);
    setIsSubmitting(false);

    const timer = setTimeout(() => setTimerElapsed(true), MIN_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [open]);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      const pendingToken = localStorage.getItem("pendingToken");
      const response = await axios.post("/login/terms", {
        PendingToken: pendingToken,
      });

      localStorage.removeItem("pendingToken");

      if (response.data?.refreshToken) {
        localStorage.setItem("refreshToken", response.data.refreshToken);
      }

      onAccepted();
    } catch {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    if (onDeclined) {
      onDeclined();
      return;
    }

    dispatch(logOut());
  };

  const acceptEnabled = scrolledToBottom && timerElapsed && !isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Terms &amp; Conditions
      </DialogTitle>
      <DialogContent sx={{ padding: 0, display: "flex", flexDirection: "column", height: "70vh" }}>
        {/* Isolated iframe so the HTML doc uses its own styles, not Shep's global styles */}
        <div style={{ flex: 1, minHeight: 0, padding: "0 20px" }}>
          <iframe
            srcDoc={htmlContent}
            title="Terms and Conditions"
            style={{ flex: 1, border: "none", width: "100%", height: "100%", minHeight: 0 }}
            onLoad={(e) => {
              const iframeDoc = e.target.contentDocument;
              const iframeWin = e.target.contentWindow;
              if (!iframeDoc || !iframeWin) return;

              if (iframeDoc.documentElement) {
                iframeDoc.documentElement.style.height = "100%";
                iframeDoc.documentElement.style.overflowY = "auto";
              }
              if (iframeDoc.body) {
                iframeDoc.body.style.height = "auto";
                iframeDoc.body.style.overflowY = "visible";
              }

              const checkScroll = () => {
                const el = iframeDoc.documentElement;
                const scrollTop = el.scrollTop;
                const scrollHeight = el.scrollHeight;
                const clientHeight = el.clientHeight;
                if (scrollHeight - scrollTop <= clientHeight + 16) setScrolledToBottom(true);
              };

              // Attach to the iframe's window 
              iframeWin.addEventListener("scroll", checkScroll);
              checkScroll();
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderTop: "1px solid var(--default-dark)",
            gap: "12px",
          }}
        >
          {scrolledToBottom && !timerElapsed && (
            <Typography variant="caption" color="text.secondary" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <LoadingAnimation size={14} /> Please wait a moment before accepting&hellip;
            </Typography>
          )}
          <div style={{ display: "flex", gap: "12px", marginLeft: "auto" }}>
            <Button variant="text" color="inherit" onClick={handleDecline} disabled={isSubmitting}>
              Decline
            </Button>
            <ShepTooltip title={!scrolledToBottom ? "Please scroll to the bottom of the document to continue." : ""}>
              <span>
                <ShepButton
                  variant="contained"
                  color="primary"
                  disabled={!acceptEnabled}
                  onClick={handleAccept}
                >
                  {isSubmitting ? <LoadingAnimation size={20} /> : "Accept"}
                </ShepButton>
              </span>
            </ShepTooltip>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TermsConditionModal;
